import { type BoxSummary, favoriteBoxLabel, VOLATILITY_WORDS } from "@loot-os/api-contract";
import type { BoxCardTopCard } from "@loot-os/design-system/domain/BoxCard";
import type { BoxCardContent } from "@loot-os/design-system/templates/shared/content";
import { useMemo } from "react";
import { useFavorites } from "../../../lib/favorites.js";

/**
 * The box's most valuable item that can drop, as the box card's top card; absent when the list sent
 * none or the item has no picture.
 */
export function topCardOf(box: BoxSummary): BoxCardTopCard | undefined {
  const item = box.topItem;
  return item?.imageUrl ? { image: item.imageUrl, alt: item.name } : undefined;
}

/** `boxes` as cards, each carrying the shopper's favorite state; null while the boxes load. */
export function useBoxCards(boxes: readonly BoxSummary[] | null): BoxCardContent[] | null {
  const { saved, loaded, toggle } = useFavorites();
  return useMemo(
    () =>
      boxes?.map((box) => ({
        box,
        href: `/boxes/${box.id}`,
        favorite: {
          // Unknown until the favorites arrive, so their arrival doesn't play every saved heart.
          saved: loaded ? saved.has(box.id) : undefined,
          label: favoriteBoxLabel(box.name),
          onToggle: () => toggle(box.id),
        },
        topCard: topCardOf(box),
        volatility: box.volatility,
        volatilityWords: VOLATILITY_WORDS,
      })) ?? null,
    [boxes, saved, loaded, toggle],
  );
}
